import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  SvgPlay,
  SvgPause,
  SvgVideoRecord,
  SvgDownloadFailed,
  SvgClose,
  SvgDownload,
} from "ui/svgs";
import { AuthContext } from "context/AuthContext";
import { chunkSize } from "lib/constants";
import { Tooltip } from "react-tooltip";
import Modal from "react-modal";
import { showToast } from "ui/components/common";
import { Line } from "rc-progress";
import { ConfirmModalStyles } from "lib/constants";
import { SinglePcManagerContext } from "context/SinglePcManagerContext";
import { ResDeviceType } from "webrtc-player/common/base";
import { getTokenByActionAndResourceId } from "lib/utils";
import {
  DownloaderWebrtcAction,
  DownloaderVideoDownloadReq,
} from "webrtc-player/common/downloader";
import { DownloadTaskType, WsResDataType } from "lib/types";
import PubSub from "pubsub-js";
import _ from "lodash";
import usePlayBackRtcService from "pages/dashboard/playback/service";

const transFileSize = (size: string | undefined | number) => {
  const sizeNum = Number(size);
  if (isNaN(sizeNum) || sizeNum < 0) {
    return "-";
  }

  if (sizeNum < 1024) {
    return sizeNum + "B";
  }
  if (sizeNum < 1000 * 1024) {
    return (Math.floor((sizeNum * 100) / 1024) / 100).toFixed(1) + "KB";
  }
  if (sizeNum < 1000 * 1024 * 1024) {
    return (Math.floor((sizeNum * 100) / 1024 / 1024) / 100).toFixed(1) + "MB";
  }
  if (sizeNum < 1000 * 1024 * 1024 * 1024) {
    return (
      (Math.floor((sizeNum * 100) / 1024 / 1024 / 1024) / 100).toFixed(1) + "GB"
    );
  }
  if (sizeNum < 1000 * 1024 * 1024 * 1024 * 1024) {
    return (
      (Math.floor((sizeNum * 100) / 1024 / 1024 / 1024 / 1024) / 100).toFixed(
        1,
      ) + "TB"
    );
  }

  return "-";
};

export default function DownloadItem({
  readOnly,
  downloadInfo,
  worker,
  downloaderId,
  onOneChunkSaved,
  onFailed,
  onFinished,
  onDeleted,
}: {
  readOnly?: boolean;
  downloadInfo: DownloadTaskType;
  worker: Worker;
  downloaderId: string;
  onOneChunkSaved?: (rec: number) => void;
  onFailed?: (err: string) => void;
  onFinished?: () => void;
  onDeleted?: (recordId?: string, fileId?: string) => void;
}) {
  const { sendDeleteDownloadRequest } = usePlayBackRtcService();
  const { getController } = useContext(SinglePcManagerContext);
  const { user } = useContext(AuthContext);
  const [wsDeleting, setWsDeleting] = useState(false);
  const [merging, setMerging] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const [recChunk, setRecChunk] = useState(-1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [retryTimes, setRetryTimes] = useState(0);
  const retryTimesRef = useRef(0);
  //acutal downloading state
  const [innerDownloading, setInnerDownloading] = useState(false);

  const startDownload = (token: string, chunk: number) => {
    setInnerDownloading(true);
    console.log("start startDownload", chunk);
    doDownloadChunk(chunk, token);
  };

  const deleteRemoteFile = async () => {
    const requests = [
      {
        actorId: user.id.toString(),
        downloaderId: downloaderId,
        recordId: downloadInfo.recordId,
        camera: {
          id: downloadInfo.camera.id,
          macAddress: downloadInfo.camera.macAddress,
          remoteIndex: downloadInfo.camera.remoteIndex,
        },
      },
    ];
    setWsDeleting(true);
    try {
      const res = await sendDeleteDownloadRequest(requests, downloadInfo.orgId);
      const data = (res as WsResDataType)?.deviceDownloaderDeleteReply?.result;
      const errorData = data?.find((e) => e?.error);
      if (errorData) {
        showToast(
          1,
          "There was a problem remove some of the downloads. Please try again.. Error:" +
            errorData?.error?.message,
        );
        return;
      }
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting downloads:", error);
      showToast(
        1,
        "There was a problem delete the download task. Please try again. Error:" +
          error?.message,
      );
    } finally {
      setWsDeleting(false);
    }
  };

  const deleteLocalFile = () => {
    onDeleted(downloadInfo.recordId, downloadInfo.fileId);
    worker.postMessage({
      action: "deleteRecordData",
      deleteRecordData: { recordId: downloadInfo.recordId },
    });

    return;
  };
  const doDownloadChunk = async (chunk, strToken) => {
    if (chunk <= recChunk && recChunk > 0) {
      return;
    }
    const req: DownloaderVideoDownloadReq = {
      token: strToken,
      speed: 32,
      downloaderId: downloaderId,
      actorId: user.id.toString(),
      recordId: downloadInfo.recordId,
      camera: {
        id: +downloadInfo.camera.id,
        macAddress: downloadInfo.camera.macAddress,
        remoteIndex: +downloadInfo.camera.remoteIndex,
      },
      //@ts-ignore
      content: {
        fileId: downloadInfo.fileId,
        ranges: {
          start: chunk * chunkSize,
          end: Math.min((chunk + 1) * chunkSize - 1, Number(downloadInfo.size)),
        },
      },
    };
    console.log("start videoDownload Chunk", downloadInfo);

    const controller = await getController(
      +downloadInfo.nvrId,
      downloadInfo.devId,
      downloadInfo.orgId,
    );
    controller?.videoDownload(req, (error, data) => {
      console.log("videoDownload callback res", error, chunk);
      if (error) {
        console.log("download error msg", error.code, error.name);
        if (
          retryTimesRef.current < 10000 &&
          !error?.external?.playbackDownloaderNoExist
        ) {
          if (!pausedRef.current) {
            console.log("videoDownload retry", retryTimesRef.current);
            retryTimesRef.current++;
            setTimeout(() => {
              setRetryTimes(retryTimesRef.current);
            }, 5000);
          }
          return;
        }
        showToast(1, "Failed to download video. Video may not exist.");
        onFailed?.(error.toString());
        return;
      }
      retryTimesRef.current = 0;
      setRetryTimes(0);
      worker.postMessage({
        action: "addChunkData",
        addChunkData: {
          recordId: data.recordId,
          fileId: data.content.fileId,
          chunk: chunk,
          data: data.payload,
        },
      });
    });
  };
  // trigger next chunk download
  useEffect(() => {
    if (retryTimes == 0 || readOnly || downloadInfo.state !== "downloading") {
      return;
    }
    downloadChunk();
  }, [retryTimes]);

  useEffect(() => {
    if (readOnly || downloadInfo.state !== "downloading") {
      return;
    }
    downloadChunk();
  }, [recChunk]);

  const downloadChunk = async () => {
    console.log("one chunk rec", recChunk);
    onOneChunkSaved?.(recChunk);
    //on last chunk downloaded
    if ((recChunk + 1) * chunkSize + 1 > Number(downloadInfo.size)) {
      console.log("video Download finnish");
      onFinished?.();
      deleteRemoteFile();
      setMerging(true);
      worker.postMessage({
        action: "startMergeData",
        startMergeData: {
          recordId: downloadInfo.recordId,
          fileId: downloadInfo.fileId,
        },
      });
      return;
    }
    const res = await getTokenByActionAndResourceId(
      [DownloaderWebrtcAction.Download],
      downloadInfo.camera.id,
      user,
      ResDeviceType.CAMERA,
      downloadInfo.orgId,
    );
    const [{ token }] = res;
    if (pausedRef.current || !token) {
      return;
    }

    const nextChunk = recChunk + 1;
    doDownloadChunk(nextChunk, token);
  };

  //start download at downloading state
  useEffect(() => {
    if (
      downloadInfo.state === "downloading" &&
      !paused &&
      !readOnly &&
      !innerDownloading
    ) {
      getTokenByActionAndResourceId(
        [DownloaderWebrtcAction.Download],
        downloadInfo.camera.id,
        user,
        ResDeviceType.CAMERA,
        downloadInfo.orgId,
      ).then((res) => {
        const token = res?.at(0)?.token;
        if (!token) {
          showToast(1, "Failed to get download token");
          return;
        }
        setRecChunk(downloadInfo.chunk);
        startDownload(token, downloadInfo.chunk + 1);
      });
    }
  }, [downloadInfo.state, paused, readOnly]);
  //fix inner state when paused
  useEffect(() => {
    setInnerDownloading(false);
  }, [paused]);
  // init events
  useEffect(() => {
    const subDownloadMerged = PubSub.subscribe(
      "onDownloadMerged",
      (_, { recordId, fileId }) => {
        if (
          recordId === downloadInfo.recordId &&
          fileId === downloadInfo.fileId
        ) {
          setMerging(false);
        }
      },
    );
    const subChunkAdded = PubSub.subscribe(
      "onChunkDataAdded",
      (_, { recordId, fileId, chunk }) => {
        if (
          recordId === downloadInfo.recordId &&
          fileId === downloadInfo.fileId
        ) {
          setRecChunk((pre) => {
            return Math.max(pre, chunk);
          });
        }
      },
    );
    return () => {
      PubSub.unsubscribe(subChunkAdded);
      PubSub.unsubscribe(subDownloadMerged);
    };
  }, []);
  //use localstorage state
  const percent = useMemo(() => {
    // if (readOnly) {
    return (
      (((downloadInfo.chunk + 1) * chunkSize + 1) / Number(downloadInfo.size)) *
      100
    );
    // } else {
    //return (((recChunk + 1) * chunkSize + 1) / Number(downloadInfo.size)) * 100;
    // }
  }, [downloadInfo.chunk]);

  return (
    <>
      <div className="pl-7 pr-5 h-full w-full flex gap-3 items-center">
        <div>
          {downloadInfo.state === "downloading" ||
          downloadInfo.state === "downloaded" ? (
            <SvgVideoRecord width="16" className="fill-neutral-500" />
          ) : downloadInfo.state === "generating" ||
            downloadInfo.state === "generated" ? (
            <SvgVideoRecord width="16" className="fill-neutral-500" />
          ) : (
            <SvgDownloadFailed width="18" className="fill-[#F73D2F]" />
          )}
        </div>
        {(downloadInfo.state === "generating" ||
          downloadInfo.state === "downloaded") && (
          <div
            data-tooltip-id={downloadInfo.recordId + downloadInfo.fileId}
            data-tooltip-content={downloadInfo.record.name}
            className="flex-grow overflow-hidden break-words text-nowrap text-ellipsis max-w-[220px]"
          >
            {downloadInfo.record.name}{" "}
            <Tooltip
              id={downloadInfo.recordId + downloadInfo.fileId}
              className=" !text-white !rounded-xl !shadow-xl"
            />
          </div>
        )}

        {downloadInfo.state === "failed" && (
          <div className="flex-grow justify-between flex mr-10">
            <div
              className="text-neutral-500 overflow-hidden break-words text-nowrap text-ellipsis max-w-[160px]"
              data-tooltip-id={downloadInfo.recordId + downloadInfo.fileId}
              data-tooltip-content={downloadInfo.record.name}
            >
              {downloadInfo.record.name}
              <Tooltip
                id={downloadInfo.recordId + downloadInfo.fileId}
                className=" !text-white !rounded-xl !shadow-xl"
              />
            </div>
            <div className="text-[#F73D2F]">Failed</div>
          </div>
        )}
        {(downloadInfo.state === "downloading" ||
          downloadInfo.state === "generated") && (
          <div className="flex items-center flex-col flex-grow w-full gap-1.5">
            <div className="flex justify-between w-full item-center">
              <div
                className="text-base overflow-hidden break-words text-nowrap text-ellipsis max-w-[110px]"
                data-tooltip-id={downloadInfo.recordId + downloadInfo.fileId}
                data-tooltip-content={downloadInfo.record.name}
              >
                {downloadInfo.record.name}
                <Tooltip
                  id={downloadInfo.recordId + downloadInfo.fileId}
                  className=" !text-white !rounded-xl !shadow-xl"
                />
              </div>
              <div className="text-neutral-500 text-sm leading-[24px]">
                {readOnly
                  ? transFileSize((downloadInfo.chunk + 1) * chunkSize)
                  : transFileSize((recChunk + 1) * chunkSize)}
                /{transFileSize(downloadInfo.size)}
              </div>
            </div>
            <div className="text-xs text-neutral-500 w-full">
              <Line
                percent={percent}
                strokeWidth={3}
                trailWidth={3}
                strokeColor={downloadInfo.chunk === -1 ? "#E1E1E1" : "#3E83F6"}
                trailColor="#E1E1E1"
              />
            </div>
          </div>
        )}

        <div className="ml-1 h-full flex items-center">
          {(downloadInfo.state === "generating" ||
            downloadInfo.state === "generated") && (
            <div className="flex gap-5 justify-between items-center w-full">
              <div
                className="ml-1 text-neutral-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
              {!readOnly && (
                <button
                  onClick={() => {
                    setDeleteModalOpen(true);
                  }}
                >
                  <SvgClose width={"16"} className="fill-neutral-500" />
                </button>
              )}
            </div>
          )}
          {downloadInfo.state === "failed" && !readOnly && (
            <button
              onClick={() => {
                setDeleteModalOpen(true);
              }}
            >
              <SvgClose width={"16"} className="fill-neutral-500" />
            </button>
          )}
          {downloadInfo.state === "downloaded" && (
            <div className="flex gap-5 justify-between items-center w-full">
              {merging ? (
                <div
                  className="ml-1 text-neutral-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                <button
                  className=""
                  onClick={() => {
                    setMerging(true);
                    worker.postMessage({
                      action: "startMergeData",
                      startMergeData: {
                        recordId: downloadInfo.recordId,
                        fileId: downloadInfo.fileId,
                      },
                    });
                  }}
                >
                  <SvgDownload width={"20"} className="fill-neutral-500" />
                </button>
              )}
              {!readOnly && (
                <button
                  className=""
                  onClick={() => {
                    setDeleteModalOpen(true);
                  }}
                >
                  <SvgClose width={"16"} className="fill-neutral-500" />
                </button>
              )}
            </div>
          )}
          {downloadInfo.state === "downloading" && !readOnly && (
            <div className="flex gap-5 justify-between items-center w-full">
              {paused ? (
                <button
                  onClick={() => {
                    pausedRef.current = false;
                    setPaused(false);
                  }}
                >
                  <SvgPlay width={"20"} className="fill-neutral-500" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    pausedRef.current = true;
                    setPaused(true);
                  }}
                >
                  <SvgPause width={"20"} className="fill-neutral-500" />
                </button>
              )}
              <button
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
              >
                <SvgClose width={"16"} className="fill-neutral-500" />
              </button>
            </div>
          )}
        </div>
        <Modal
          isOpen={deleteModalOpen}
          style={ConfirmModalStyles}
          contentLabel="Example Modal"
        >
          <h1 className="text-xl font-bold">
            {downloadInfo.state === "generating" ||
            downloadInfo.state === "generated" ||
            downloadInfo.state === "downloading"
              ? "Cancel download?"
              : downloadInfo.state === "failed" ||
                  downloadInfo.state === "downloaded"
                ? "Remove download?"
                : ""}
          </h1>
          <div className="text-base mt-4">
            {downloadInfo.state === "generating" ||
            downloadInfo.state === "generated" ||
            downloadInfo.state === "downloading"
              ? "Are you sure you want to cancel the download?"
              : downloadInfo.state === "failed" ||
                  downloadInfo.state === "downloaded"
                ? "Are you sure you want to remove the download?"
                : ""}
          </div>

          <div className="flex justify-end mt-6">
            <button
              className="rounded-2xl bg-white flex justify-center items-center h-[52px] w-[172px] border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                if (downloadInfo.state !== "downloaded") {
                  deleteLocalFile();
                  deleteRemoteFile();
                } else {
                  deleteLocalFile();
                  setDeleteModalOpen(false);
                }
              }}
              disabled={wsDeleting}
            >
              {wsDeleting ? (
                <div
                  className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : downloadInfo.state === "generating" ||
                downloadInfo.state === "generated" ||
                downloadInfo.state === "downloading" ? (
                "Cancel Download"
              ) : downloadInfo.state === "failed" ||
                downloadInfo.state === "downloaded" ? (
                "Remove Download?"
              ) : (
                ""
              )}
            </button>
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                setDeleteModalOpen(false);
              }}
              disabled={wsDeleting}
            >
              {downloadInfo.state === "generating" ||
              downloadInfo.state === "generated" ||
              downloadInfo.state === "downloading"
                ? "Continue Download"
                : downloadInfo.state === "failed" ||
                    downloadInfo.state === "downloaded"
                  ? "Cancel"
                  : ""}
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
}
